import { Children, type ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { User } from '@vd/types';
import { renderMentions } from '../mentions/renderMentions';
import styles from './MarkdownBody.module.css';

/**
 * Render a comment / response body as markdown with @-mention highlighting.
 *
 * Comments authored by Claude Code (via the MCP server's `add_comment`
 * tool) are markdown by convention — agents naturally write
 * `**bold**`, lists, code blocks, and the rest. Pre-extraction we
 * rendered everything as plain text with `white-space: pre-wrap`,
 * which produced literal `**` and unparsed list bullets in the UI.
 *
 * Now: react-markdown builds a React tree (no `dangerouslySetInnerHTML`
 * — inherently safe by default; raw HTML in the source is stripped),
 * remark-gfm extends the parser to handle GitHub-flavored markdown
 * (tables, strikethrough, task lists, autolinked URLs).
 *
 * @-mention integration: react-markdown's text children are plain
 * strings. We override the inline-text-bearing element renderers
 * (paragraphs, list items, headings, emphasized text) to walk children
 * and replace string segments with the styled mention spans from
 * `renderMentions`. Mentions inside code blocks intentionally do NOT
 * get the pill treatment (a `@username` inside a `<code>` block is
 * almost certainly literal text the author wants preserved verbatim).
 *
 * If the body has no markdown features, react-markdown still produces
 * sensible output — a single `<p>` containing the text.
 */
export interface MarkdownBodyProps {
  body: string;
  members: Pick<User, 'id' | 'name'>[];
  /**
   * False where this is a formatted copy of text a keyboard reaches some other
   * way, such as a ticket's description over its textarea: its links stay
   * clickable and out of the Tab order.
   */
  linksFocusable?: boolean;
}

/**
 * Walk a single React node (string | element | array) and replace any
 * string segment with renderMentions output. Non-string nodes pass
 * through unchanged. The recursion stays shallow because react-markdown
 * already produced the tree — we're only inserting at the leaf-string
 * level. Returns ReactNode[] so the caller can spread into JSX.
 */
function processChildren(
  children: ReactNode,
  members: Pick<User, 'id' | 'name'>[],
): ReactNode {
  // react-markdown passes children as ReactNode (string, element, or
  // array). Children.toArray flattens arrays + drops nulls cleanly.
  const arr = Children.toArray(children);
  const out: ReactNode[] = [];
  for (let i = 0; i < arr.length; i++) {
    const child = arr[i];
    if (typeof child === 'string') {
      out.push(...renderMentions(child, members));
    } else {
      out.push(child);
    }
  }
  return out;
}

/**
 * A link's address, when it is one this page will follow: http(s), mail, or a
 * path on this site (security double check, 2026-10-11). react-markdown's own
 * transform already drops `javascript:` and `data:`; this holds without it, so
 * a later change to that configuration cannot turn a widget visitor's words
 * into a script link in a teammate's browser.
 */
export function safeHref(url: string | undefined): string | undefined {
  if (!url) return undefined;
  return /^(https?:|mailto:|\/(?!\/))/i.test(url.trim()) ? url : undefined;
}

export function MarkdownBody({ body, members, linksFocusable = true }: MarkdownBodyProps) {
  return (
    <div className={styles.markdown}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // The inline + block elements that contain user-facing prose
          // get the @-mention sweep. Code blocks deliberately do not —
          // mention-shaped strings inside code are usually literal.
          p: ({ children }) => <p>{processChildren(children, members)}</p>,
          li: ({ children }) => <li>{processChildren(children, members)}</li>,
          strong: ({ children }) => <strong>{processChildren(children, members)}</strong>,
          em: ({ children }) => <em>{processChildren(children, members)}</em>,
          h1: ({ children }) => <h1>{processChildren(children, members)}</h1>,
          h2: ({ children }) => <h2>{processChildren(children, members)}</h2>,
          h3: ({ children }) => <h3>{processChildren(children, members)}</h3>,
          h4: ({ children }) => <h4>{processChildren(children, members)}</h4>,
          h5: ({ children }) => <h5>{processChildren(children, members)}</h5>,
          h6: ({ children }) => <h6>{processChildren(children, members)}</h6>,
          // Links are markdown's `[text](url)` shape. Force noopener +
          // target=_blank so clicking a link in a comment doesn't
          // navigate the ticket page away. rel="noreferrer" hides our
          // app URL from the destination's referer.
          a: ({ children, href }) => (
            <a href={safeHref(href)} target="_blank" rel="noopener noreferrer" tabIndex={linksFocusable ? undefined : -1}>
              {processChildren(children, members)}
            </a>
          ),
          // An image is a link to it, never loaded (security double check,
          // 2026-10-11). A widget visitor's report lands in a description
          // word for word, and an image there loaded in the browser of every
          // teammate who opened the ticket: it told its author who read it and
          // when, and could draw a fake sign-in panel. Nothing Toado writes
          // uses markdown images.
          img: ({ src, alt }) => {
            const href = safeHref(typeof src === 'string' ? src : undefined);
            const label = alt ? `Image: ${alt}` : 'Image';
            return href ? (
              <a href={href} target="_blank" rel="noopener noreferrer" tabIndex={linksFocusable ? undefined : -1}>{label}</a>
            ) : <span>{label}</span>;
          },
        }}
      >
        {body}
      </ReactMarkdown>
    </div>
  );
}
